import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Button,
  CssBaseline,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import SettingsIcon from "@mui/icons-material/Settings";
import { useNavigate, Link } from "react-router-dom";
import Grocerylogo from "../images/Grocerylogo.png";
import useLiveLocation from "./useLiveLocation";

const Header3 = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { location, loading } = useLiveLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // ✅ Safe user fetching
  const user = JSON.parse(localStorage.getItem("user")) || {};

  const customerName =
    user?.name ||
    user?.customer?.name ||
    user?.data?.name ||
    user?.user?.name ||
    "Customer";
  const profileImg =
    user?.profileImageUrl ||
    user?.customer?.profileImageUrl ||
    user?.user?.profileImageUrl ||
    "";

  // ✅ Handlers
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/MyAccountsSignIn");
  };

  const handleProfileUpdate = () => {
    navigate("/updateCustomerProfile");
    setAnchorEl(null);
  };

  const handleAccountSettings = () => {
    navigate("/MyAccountSetting");
    setAnchorEl(null);
    setMobileOpen(false);
  };

  const handleMenuClick = (route) => {
    navigate(route);
    setAnchorEl(null);
    setMobileOpen(false);
  };

  // 📱 Mobile Drawer Menu
  const drawer = (
    <Box
      sx={{ width: 250, bgcolor: "#f9f9f9", height: "100%",p:8}}
      role="presentation"
      onClick={() => setMobileOpen(false)}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: 2,
          bgcolor: "#1b5e20",
          color: "white",
        }}
      >
        <Avatar
          src={profileImg}
          sx={{
            width: 80,
            height: 40,
            bgcolor: "#4caf50",
            mr: 1,
          }}
        >
          {customerName?.charAt(0)?.toUpperCase()}
        </Avatar>
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {customerName}
          </Typography>
        
        </Box>
      </Box>

      <Divider />

      <List>
        <ListItem button onClick={() => handleMenuClick("/")}>
          <HomeIcon sx={{ mr: 1, color: "#1b5e20" }} />
          <ListItemText primary="Home" />
        </ListItem>

        <ListItem button onClick={() => handleMenuClick("/updateCustomerProfile")}>
          <PersonIcon sx={{ mr: 1, color: "#1b5e20" }} />
          <ListItemText primary="Profile Update" />
        </ListItem>

        <ListItem button onClick={handleAccountSettings}>
          <SettingsIcon sx={{ mr: 1, color: "#1b5e20" }} />
          <ListItemText primary="Account Settings" />
        </ListItem>

        <Divider />

        <ListItem button onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 1, color: "red" }} />
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          bgcolor: "#1b5e20",
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
          zIndex: (theme) => theme.zIndex.drawer + 2,
        }}
      >
        <Toolbar
          sx={{
            px: { xs: 2, sm: 4 },
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Left Section: Logo + Mobile Menu */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {isMobile && (
              <IconButton
                color="inherit"
                edge="start"
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Link to="/">
              <Box
                component="img"
                src={Grocerylogo}
                alt="Grocery Logo"
                sx={{
                  height: { xs: 45, sm: 55 },
                  width: "auto",
                  objectFit: "contain",
                }}
              />
            </Link>
              
          </Box>

          {/* Right Section: Welcome + Location + Avatar */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 2 },
            }}
          >
            {!isMobile && (
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 500,
                  fontSize: { xs: 12, sm: 14 },
                  fontFamily: "Poppins",
                }}
              >
                👋 Welcome, {customerName}
              </Typography>
            )}

            <Button
              sx={{
                color: "#fff",
                textTransform: "none",
                fontSize: { xs: "12px", sm: "13px" },
              }}
            >
              📍{" "}
              {loading ? (
                <CircularProgress size={16} sx={{ color: "white", ml: 1 }} />
              ) : (
                <Box component="span" sx={{ ml: 1 }}>
                  {location || "Set Location"}
                </Box>
              )}
            </Button>

            <IconButton
              color="inherit"
              onClick={handleAccountSettings}
              aria-label="open account settings"
            >
              <SettingsIcon />
            </IconButton>

            {/* Avatar Menu */}
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
              <Avatar
                src={profileImg}
                sx={{
                  bgcolor: "#4caf50",
                  width: 36,
                  height: 36,
                  border: "2px solid white",
                }}
              >
                {customerName?.charAt(0)?.toUpperCase()}
              </Avatar>
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={handleProfileUpdate}>
                <PersonIcon fontSize="small" sx={{ mr: 1 }} /> Profile Update
              </MenuItem>
              <MenuItem onClick={handleAccountSettings}>
                <SettingsIcon fontSize="small" sx={{ mr: 1 }} /> Account Settings
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <LogoutIcon fontSize="small" sx={{ mr: 1, color: "red" }} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 250,
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header3;
